<template>
  <div data-test-id="featured-book" class="featured-book p-9 flex flex-col m:flex-row">
    <a :href="book.previewUrl" target="_blank" class="featured-book__cover mx-auto m:mx-0 mb-8">
      <book-cover class="featured-book__image block w-full" :sources="book.image" />
      <div v-if="!isWebViewOnMobile" class="featured-book__read-it">
        {{ $t("coverCta") }}
      </div>
    </a>
    <div class="m:ml-8">
      <div class="featured-book__header">
        <div class="featured-book__headline">
          {{ book.title }}
        </div>
        <div class="featured-book__author mb-6">{{ $t("by") }} {{ book.author }}</div>
      </div>
      <div class="featured-book__body">
        <featured-book-stats class="mb-8" :book="book" />
        <audio-player
          :book-slug="book.slug"
          theme="dark"
          :label="$t('keyIdeaNumber', { chaptersLength: book.chaptersLength })"
        />

        <a class="featured-book__cta cta" :href="book.previewUrl" target="_blank">
          {{ cta }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import AudioPlayer from "@/features/books-preview-audio/books-preview-audio"
import FeaturedBookStats from "@/features/featured-book/components/featured-book-stats"
import BookCover from "@/shared/book-cover"
import { mapGetters } from "vuex"

export const messages = {
  en: {
    keyIdeaNumber: "Listen to key idea 1 of {chaptersLength}",
    coverCta: "Discover it on Blinkist",
    by: "by",
    cta: {
      web: "Get all key ideas on Blinkist",
      mobile: "Open Blinks"
    }
  },
  de: {
    coverCta: "Jetzt bei Blinkist entdecken",
    by: "von",
    keyIdeaNumber: "Kernaussage 1 von {chaptersLength} anhören",
    cta: {
      web: "Erhalte Zugang zu allen Blinks",
      mobile: "Open Blinks"
    }
  },
  es: {
    coverCta: "Descúbrelo en Blinkist",
    by: "de",
    keyIdeaNumber: "Escucha la idea clave 1 de {chaptersLength}",
    cta: {
      web: "Accede a todas las ideas clave en Blinkist",
      mobile: "Open Blinks"
    }
  }
}

export default {
  name: "FeaturedBook",
  components: {
    FeaturedBookStats,
    BookCover,
    AudioPlayer
  },
  props: {
    book: {
      type: Object,
      default: () => {},
      required: true
    },
    platform: {
      type: String,
      default: "web"
    }
  },
  computed: {
    ...mapGetters(["abtests"]),
    isWebViewOnMobile() {
      return this.platform === "mobile"
    },
    cta() {
      if (this.isWebViewOnMobile) {
        return this.$t("cta.mobile")
      } else {
        return this.$t("cta.web")
      }
    }
  },
  i18n: { messages }
}
</script>

<style lang="scss" scoped>
.featured-book {
  $root: &;

  @include font-family(sans-serif);

  border: $spacing-8 solid $pale-mint-grey;

  &__cover {
    position: relative;

    display: block;
    align-self: flex-start;

    max-width: spacing(176px);

    &:hover {
      #{$root}__read-it {
        display: flex;
      }
    }
  }

  &__read-it {
    position: absolute;
    top: 0;
    left: 0;

    display: none;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: $spacing-8;

    color: $white;

    background-color: rgba($deep-black, 0.8);
  }

  &__headline {
    @include font-style(h3);
    @include cursored;

    margin-bottom: $spacing-16;

    &:after {
      margin-top: $spacing-16;
    }
  }

  &__author {
    @include font-style(caption, $color: $midnight);
  }

  &__body {
    @include font-style(caption, $color: $midnight);
  }

  &__cta.cta {
    text-decoration: none;

    @include media(max-width, m) {
      width: 100%;
      min-width: auto;
    }
  }
}
</style>
